import { ChangeEvent, useState, useEffect } from 'react';
import { css, useTheme } from '@emotion/react';
import { Link, MagicWand } from '@phosphor-icons/react';
import { useProjectState } from 'client/state/ProjectState';
import { produceState } from 'client/state/stateUtils';
import { Collapsible } from 'client/components/Collapsible';
import { Divider } from 'client/components/Divider';
import { Help } from 'client/components/Help';
import { PostfixedInput } from 'client/components/Input';
import { Toggle } from 'client/components/Toggle';

export const ProjectDomain = ({ isCreate = false }: { isCreate?: boolean }) => {
  const theme = useTheme();
  const tweakedDomainSlug = useProjectState((state) => state.document?.tweakedDomainSlug ?? null);
  const defaultDomain = useProjectState((state) => state.document?.defaultDomain || '');
  const [tweakedSlugCache, setTweakedSlugCache] = useState(tweakedDomainSlug);

  const defaultDomainSlug = defaultDomain.split('.')[0];
  const tweakedDomainRoot = `.${defaultDomain.split('.').slice(1).join('.')}`;
  const isTweakedDomain = tweakedDomainSlug !== null;

  const handleUseDefaultToggle = produceState(useProjectState, (state, checked: boolean) => {
    if (checked) {
      state.document.tweakedDomainSlug = null;
    } else {
      state.document.tweakedDomainSlug = tweakedSlugCache || defaultDomainSlug;
    }
  });

  const handleDomainChange = produceState(useProjectState, (state, event: ChangeEvent<HTMLInputElement>) => {
    state.document.tweakedDomainSlug = event.target.value;
  });

  useEffect(() => {
    if (tweakedDomainSlug) setTweakedSlugCache(tweakedDomainSlug);
  }, [tweakedDomainSlug]);

  if (isCreate) return null;

  return (
    <>
      <Toggle
        title="Use Auto-Configured Parent Domain"
        subtitle={
          <>
            <Link weight="bold" />
            {defaultDomain}
          </>
        }
        checked={!isTweakedDomain}
        icon={<MagicWand color={theme.color.grey[100]} />}
        onChange={handleUseDefaultToggle}
      />

      <Collapsible open={isTweakedDomain}>
        <Divider />

        <PostfixedInput
          path="tweakedDomainSlug"
          label="Custom Domain"
          postfix={tweakedDomainRoot}
          value={tweakedDomainSlug ?? tweakedSlugCache ?? defaultDomainSlug}
          onChange={handleDomainChange}
          css={inputStyle}
        />
      </Collapsible>

      <Help>Deployments can still be configured with custom domains, even if this setting is enabled.</Help>
    </>
  );
};

const inputStyle = css`
  > .postfix {
    // For some reason the postfix is too high on this specific input, not on any of the others...
    bottom: 7px;
  }
`;
